import React from "react";
import { useLocation } from "react-router-dom";
import SidebarSkeleton from "./SidebarSkeleton";
import {
  readStoredSidebarOpen,
  writeStoredSidebarOpen,
  readStoredSidebarWidth,
  SIDEBAR_RAIL_PX,
} from "./SidebarContext";

// Import page-specific skeleton loaders from their respective folders
import MatrixSkeletonLoader, {
  MatrixEditorSkeletonLoader,
} from "./Matrix/MatrixSkeletonLoader";
import ChatSkeletonLoader from "./Chat/ChatSkeletonLoader";
import ChatsHubSkeletonLoader from "./Chat/ChatsHubSkeletonLoader";
import ConversationSkeletonLoader from "./Chat/ConversationSkeletonLoader";
import ReportsSkeletonLoader, {
  ReportEditorSkeletonLoader,
  ReportTemplateEditorSkeletonLoader,
} from "./Reports/ReportsSkeletonLoader";

import DocumentsSkeletonLoader from "./Documents/DocumentsSkeletonLoader";
import IntakeSkeletonLoader from "./Intake/IntakeSkeletonLoader";
import { usePhiDocumentReview } from "@/deploymentView";
import { isIntakeDocumentPath } from "@/utils/productRoutes";
import SettingsSkeletonLoader from "./Settings/SettingsSkeletonLoader";
import HomeSkeletonLoader from "./Home/HomeSkeletonLoader";
import AlertsSkeletonLoader from "./Alerts/AlertsSkeletonLoader";
import AlertEditorSkeletonLoader from "./Alerts/AlertEditorSkeletonLoader";
import {
  DashboardsSkeleton,
  storedDashboardsMode,
} from "./RMS/Dashboards/DashboardsSkeleton";

// The page-area placeholder for the current URL, without the shell around it.
// SkeletonLoader wraps it in a full-screen frame with its own sidebar for the
// cold load; MainApp shows it on its own inside the loaded shell while a
// page's code is fetched (#12239), where a second sidebar would be wrong.
export const PageSkeleton: React.FC = () => {
  const location = useLocation();
  const phiDocumentReview = usePhiDocumentReview();

  // Determine which page skeleton to show based on the current URL
  const getPageSkeleton = () => {
    const pathname = location.pathname;

    if (pathname.startsWith("/matrix")) {
      // Use MatrixEditorSkeletonLoader for /matrix/:workspaceId, MatrixSkeletonLoader for /matrix
      if (pathname.match(/^\/matrix\/[^\/]+$/)) {
        return <MatrixEditorSkeletonLoader />;
      }
      return <MatrixSkeletonLoader />;
    }
    if (pathname.startsWith("/chats")) {
      // The phone Chats hub (#11742 round 3) — a list surface, not the
      // starter, so it gets its own skeleton with the hamburger.
      return <ChatsHubSkeletonLoader />;
    }
    if (pathname.startsWith("/chat")) {
      // Use ConversationSkeletonLoader for /chat/:workspaceId, ChatSkeletonLoader for /chat
      if (pathname.match(/^\/chat\/[^\/]+$/)) {
        return <ConversationSkeletonLoader />;
      }
      return <ChatSkeletonLoader />;
    }
    if (pathname.startsWith("/reports")) {
      // Use ReportEditorSkeletonLoader for /reports/:workspaceId, ReportsSkeletonLoader for /reports
      if (pathname.match(/^\/reports\/[^\/]+$/)) {
        return <ReportEditorSkeletonLoader />;
      }
      return <ReportsSkeletonLoader />;
    }
    if (pathname.startsWith("/report-templates")) {
      if (pathname.match(/^\/report-templates\/[^\/]+$/)) {
        return <ReportTemplateEditorSkeletonLoader />;
      }
      return <ReportsSkeletonLoader />;
    }
    if (pathname.startsWith("/matrix-templates")) {
      return <MatrixSkeletonLoader />;
    }
    if (pathname.startsWith("/documents")) {
      // A deployment that reviews PHI documents shows the intake queue here
      // (#11422 round 19, R8), so its placeholder is the queue's.
      if (phiDocumentReview) {
        return <IntakeSkeletonLoader review={isIntakeDocumentPath(pathname)} />;
      }
      return <DocumentsSkeletonLoader />;
    }
    if (pathname.startsWith("/settings")) {
      return <SettingsSkeletonLoader />;
    }
    if (pathname.startsWith("/alerts")) {
      // Use AlertEditorSkeletonLoader for /alerts/editor/*, AlertsSkeletonLoader for /alerts
      if (pathname.startsWith("/alerts/editor")) {
        return <AlertEditorSkeletonLoader />;
      }
      return <AlertsSkeletonLoader />;
    }
    if (pathname === "/" || pathname.startsWith("/home")) {
      return <HomeSkeletonLoader />;
    }
    if (pathname === "/rms/dashboards") {
      // The Dashboards list in the mode it opens in, rows or cards (polish-4 requirement 19).
      return <DashboardsListSkeletonLoader />;
    }

    // Default skeleton for unknown pages
    return <DefaultSkeletonLoader />;
  };

  return getPageSkeleton();
};

const SkeletonLoader: React.FC = () => {
  // This renders above SidebarProvider, so the persisted value is read directly
  // rather than through the context. Expanding here writes it back, which is
  // what the provider picks up when the app shell finally mounts.
  const [sidebarOpen, setSidebarOpen] = React.useState(readStoredSidebarOpen);
  const sidebarWidth = React.useMemo(readStoredSidebarWidth, []);

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fp-bg)] overflow-x-hidden">
      <div className="w-full h-full">
        <div className="flex h-full">
          {/* Sidebar Skeleton - fixed position */}
          <div
            // max-md:hidden (#11742): below 1024px the shell reserves no
            // sidebar column, so the placeholder must not paint one.
            // Flush to the edge and full height, as the loaded column is.
            className="fixed top-0 left-0 h-full z-20 max-md:hidden"
            style={{
              width: sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX,
            }}
          >
            <SidebarSkeleton
              collapsed={!sidebarOpen}
              onExpand={() => {
                writeStoredSidebarOpen(true);
                setSidebarOpen(true);
              }}
            />
          </div>

          {/* Main Content Area - with margin to account for fixed sidebar */}
          <div
            className="flex-1 relative h-full max-md:!ml-0"
            style={{
              marginLeft: sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX,
            }}
          >
            {/* Main content area, flush to the column */}
            <div className="w-full h-full flex overflow-auto">
              {/* Main Content - full width */}
              <div className="w-full h-full">
                <PageSkeleton />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Default skeleton for unknown pages
/** The Dashboards list's page: its head, then the list's own skeleton in the stored mode. */
const DashboardsListSkeletonLoader: React.FC = () => (
  <div
    className="h-full flex flex-col"
    style={{
      background: "var(--fp-bg)",
      padding: "24px var(--fp-page-gutter)",
    }}
  >
    <div className="mb-6 animate-pulse">
      <div className="h-7 bg-gray-200 rounded-lg w-48 mb-3" />
      <div className="h-3 bg-gray-200 rounded w-64" />
    </div>
    <DashboardsSkeleton mode={storedDashboardsMode()} />
  </div>
);

const DefaultSkeletonLoader: React.FC = () => {
  return (
    <div className="h-full overflow-auto bg-white">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-8 min-h-full">
        {/* Header skeleton */}
        <div className="mb-8">
          <div className="h-8 bg-gray-200 rounded-lg w-1/3 mb-4 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
        </div>

        {/* Content skeleton */}
        <div className="space-y-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 p-6"
            >
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkeletonLoader;
